/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateTrendingNowV2Config } from 'state/thunks/trendingnowv2'
// utils
import { createThemeColors, getHueFromHex, isLight } from 'utils/theme'
// material
import ConfigV2Controller from 'material/expandablesection/v2/configv2controller/configv2controller'
import ExpandableSection from 'material/expandablesection/expandablesection'
import { ReactComponent as ColorIcon } from 'assets/icons/color.svg'
// content
// import Mode from './mode/mode'
import Fonts from './fonts/fonts'
import Colors from './colors/colors';
import Space from 'material/space/space';
import SetTheme from 'material/settheme/settheme';
import ThemePreview from './themepreview/themepreview'
// styles
import injectSheet from 'react-jss'
import styles from './sitethemesection_styles'
import { setTempDisplayConfig, setTempTrendingNowConfig } from 'state/slices/trendingnowv2'
import { isObjectArrayEqual } from 'utils/isObjectArrayEqual'
import Language from './language/language'


/* ========== ~~~~~~~~~~ TRENDING NOW CONFIG V2: SITE THEME SECTION ~~~~~~~~~~ ========== */
const SiteThemeSection = injectSheet(styles)((props) => {

  const dispatch = useDispatch()
  const sidebarOpen = useSelector(state => state.app.sidebarOpen)
  const { displayConfig: savedDisplayConfig, trendingNowId } = useSelector(state => state.trendingNowV2.trendingNowConfig)
  const { displayConfig: tempDisplayConfig } = useSelector(state => state.trendingNowV2.tempTrendingNowConfig)
  const [siteTheme, setSiteTheme] = useState({
    ...savedDisplayConfig,
    backgroundColor: savedDisplayConfig.backgroundColor,
    primaryColor: savedDisplayConfig.primaryColor
  })

  const [isUpdating, setIsUpdating] = useState(false)
  const [isOpened, setIsOpened] = useState(false)

  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = useCallback(async (e) => {
    e.stopPropagation()
    setIsUpdating(true)
    const displayConfigRequest = {
        displayConfig: {
          ...tempDisplayConfig,
          primaryColor: tempDisplayConfig.primaryColor || '#061240',
          backgroundColor: tempDisplayConfig.backgroundColor || '#FFFFFF'
        }, 
        section: 'DISPLAY_CONFIG' 
    }
    const response = await dispatch(updateTrendingNowV2Config({ trendingNowConfig: {...displayConfigRequest}, trendingNowId }))
    if(!response.error && !response.payload.error) setIsOpened(false)
    setIsUpdating(false)
  }, [tempDisplayConfig, dispatch, trendingNowId])

  const handleCancel = useCallback(async() => {
    const displayConfig = {
      ...savedDisplayConfig,
      backgroundColor: savedDisplayConfig.backgroundColor,
      primaryColor: savedDisplayConfig.primaryColor
    }
    setIsOpened(false)
    setSiteTheme(displayConfig)
    dispatch(setTempTrendingNowConfig({
      configName: 'displayConfig',
      data: savedDisplayConfig
    }))
  }, [dispatch, savedDisplayConfig])

  const isChanged = useMemo(() => {
    const isFieldsChanged = !isObjectArrayEqual(tempDisplayConfig, savedDisplayConfig)
    return isFieldsChanged
  }, [savedDisplayConfig, tempDisplayConfig])

  const themeColors = useMemo(() => createThemeColors(!isLight(siteTheme.backgroundColor), getHueFromHex(siteTheme.primaryColor), !!siteTheme.enableMonochrome), [siteTheme])

  const setSiteThemeConfig = useCallback((key, value) => {
    setSiteTheme({...siteTheme, [key]: value})
    dispatch(setTempTrendingNowConfig({
      configName: 'displayConfig',
      data: {
        ...tempDisplayConfig,
        [key]: value
      }
    }))
  }, [dispatch, siteTheme, tempDisplayConfig])

  const setSiteThemeConfigs = useCallback((configObj) => {
    setSiteTheme({...siteTheme, ...configObj})
    dispatch(setTempTrendingNowConfig({
      configName: 'displayConfig',
      data: {
        ...tempDisplayConfig,
        ...configObj
      }
    }))
  }, [dispatch, siteTheme, tempDisplayConfig])

  const controller = (
    <ConfigV2Controller
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating}
      disableSave={!isChanged}
      saveTitle={'Save Changes'}
      icon={<ColorIcon />}
      title='Site Theme'
    />
  );

  return (
    <ExpandableSection
      className={`expandablesectionv2 sitethemesection ${props.classes.sitethemesection}`}
      controller={controller}
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating}
      disableSave={!isChanged}
    >
      <Space gap={16}>
        <Space klass={`stylesection sidebarOpen-${!!sidebarOpen}`} gap={48} direction={'column'}>
          <Colors 
            isConfigOpen={isOpened}
            siteTheme={siteTheme} 
            setSiteThemeConfig={setSiteThemeConfig} 
            setSiteThemeConfigs={setSiteThemeConfigs}
          />
          <Fonts siteTheme={siteTheme} setSiteThemeConfig={setSiteThemeConfig} />
          <Language siteTheme={siteTheme} setSiteThemeConfig={setSiteThemeConfig} />
          <SetTheme />
        </Space>
        <Space klass={'themesection'} direction={'column'}>
         <ThemePreview siteTheme={siteTheme} themeColors={themeColors} />
        </Space>
      </Space>
      
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default SiteThemeSection;